import { useState, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';
import { useAuthStore } from '../stores/authStore';
import { formatApi } from '../services/api';
import { getFormatCost } from '../types';

interface FormatNodePanelProps {
  compact?: boolean; // true = inline button (PlayerStats); false = full card (Profile)
}

export default function FormatNodePanel({ compact = false }: FormatNodePanelProps) {
  const { player, loadPlayer } = useGameStore();
  const { userId } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleInitiate = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      await formatApi.initiate(userId);
      await loadPlayer(); // node enters shutdown — the overlay takes over
    } catch (err: unknown) {
      const e = err as { response?: { data?: string | { message?: string } } };
      const msg = typeof e?.response?.data === 'string'
        ? e.response.data
        : (e?.response?.data as { message?: string })?.message ?? 'Failed to format node';
      setError(msg);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }, [userId, loadPlayer]);

  if (!player) return null;

  const cost      = getFormatCost(player.formatCount ?? 0);
  const canAfford = player.credits >= cost;

  const lastFmt    = player.lastFormatAt ? new Date(player.lastFormatAt).getTime() : 0;
  const cooldownMs = 24 * 60 * 60 * 1000;
  const onCooldown = lastFmt > 0 && (Date.now() - lastFmt) < cooldownMs;
  const cdLeft     = onCooldown ? cooldownMs - (Date.now() - lastFmt) : 0;
  const cdHrs      = Math.floor(cdLeft / 3_600_000);
  const cdMin      = Math.floor((cdLeft % 3_600_000) / 60_000);

  const btn = (bg: string, fg: string): React.CSSProperties => ({
    background: bg, color: fg, border: 'none', padding: '0.1rem 0.45rem',
    borderRadius: '3px', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'monospace', fontSize: '0.7rem',
  });

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        {onCooldown ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#555' }}>
            Format cooldown: {cdHrs}h {cdMin}m
          </span>
        ) : confirming ? (
          <>
            <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#f87171' }}>Wipe node?</span>
            <button onClick={handleInitiate} disabled={busy} style={btn('#7f1d1d', '#fca5a5')}>{busy ? '...' : 'Yes'}</button>
            <button onClick={() => setConfirming(false)} disabled={busy} style={btn('#1a1a2e', '#9ca3af')}>No</button>
          </>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            disabled={busy || !canAfford}
            title={cost === 0 ? 'First format is free' : `Cost: ${cost.toLocaleString()} cr`}
            style={{
              background: 'transparent',
              border: `1px solid ${canAfford ? '#ef4444' : '#555'}`,
              color: canAfford ? '#f87171' : '#555',
              padding: '0.1rem 0.45rem', borderRadius: '3px',
              cursor: canAfford && !busy ? 'pointer' : 'not-allowed',
              fontFamily: 'monospace', fontSize: '0.7rem',
            }}
          >
            {cost === 0 ? 'Format node (free)' : `Format node (${cost.toLocaleString()} cr)`}
          </button>
        )}
        {error && <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#f87171' }}>{error}</span>}
      </div>
    );
  }

  // ── Full card (Profile) ───────────────────────────────────────────────────────
  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header">
        <h2 className="card-title">Format Node</h2>
      </div>

      <div style={{ fontFamily: 'monospace', display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Formats performed</span>
          <span>{player.formatCount ?? 0}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Next format cost</span>
          <span style={{ color: cost === 0 ? '#4ade80' : canAfford ? '#e2e8f0' : '#f87171' }}>
            {cost === 0 ? 'Free' : `${cost.toLocaleString()} cr`}
          </span>
        </div>

        {onCooldown && (
          <div style={{ color: '#555', fontSize: '0.8rem' }}>
            Next format available in {cdHrs}h {cdMin}m
          </div>
        )}

        {error && <div style={{ color: '#f87171', fontSize: '0.82rem' }}>{error}</div>}

        {!onCooldown && (confirming ? (
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <span style={{ color: '#f87171', fontSize: '0.85rem' }}>Wipe the entire local drive?</span>
            <button onClick={handleInitiate} disabled={busy}
              style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #ef4444', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'monospace', fontWeight: 'bold' }}>
              {busy ? 'Formatting…' : 'Confirm wipe'}
            </button>
            <button onClick={() => setConfirming(false)} disabled={busy}
              style={{ background: '#1a1a2e', color: '#9ca3af', border: '1px solid #333', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: 'pointer', fontFamily: 'monospace' }}>
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            disabled={busy || !canAfford}
            style={{
              background: !canAfford ? '#1a1a2e' : '#450a0a',
              color: !canAfford ? '#555' : '#fca5a5',
              border: `1px solid ${!canAfford ? '#333' : '#ef4444'}`,
              padding: '0.55rem 1.25rem', borderRadius: '4px',
              cursor: !busy && canAfford ? 'pointer' : 'not-allowed',
              fontFamily: 'monospace', fontWeight: 'bold', width: '100%',
            }}
          >
            {cost === 0 ? 'Format Node (free)' : `Format Node — ${cost.toLocaleString()} cr`}
          </button>
        ))}

        <p style={{ color: '#555', fontSize: '0.75rem', lineHeight: '1.5', margin: 0 }}>
          Formatting wipes your local drive — all installed/running software, files and incoming
          infections. Your <strong>IP, hacked-nodes list and encrypted HD are kept</strong>. The node
          goes offline (shutdown) for 30 minutes and no action is possible until it reboots.
          Free the first time, then the cost rises and there is a 24-hour cooldown between formats.
        </p>
      </div>
    </div>
  );
}
